import React, { useEffect } from 'react';

const getJwtFromCookie = () => {
  const match = document.cookie.match(/(?:^|; )jwt=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : null;
};

const getJwtFromUrl = () => {
  // Extract JWT directly from the URL to avoid URL encoding/decoding issues
  const url = window.location.href;
  const jwtMatch = url.match(/[?&]jwt=([^&]+)/);
  if (jwtMatch) {
    const rawJwt = jwtMatch[1];
    console.log('=== JWT EXTRACTION DEBUG ===');
    console.log('Raw JWT from URL:', rawJwt.substring(0, 20) + '...');
    console.log('Raw JWT length:', rawJwt.length);
    console.log('Raw JWT contains dots:', (rawJwt.match(/\./g) || []).length);
    
    // Decode the JWT parameter manually to preserve special characters
    const decodedJwt = decodeURIComponent(rawJwt);
    console.log('Decoded JWT length:', decodedJwt.length);
    console.log('Decoded JWT contains dots:', (decodedJwt.match(/\./g) || []).length);
    console.log('=== END JWT EXTRACTION DEBUG ===');
    
    return decodedJwt;
  }
  return null;
};

const setJwtCookie = (jwt: string) => {
  const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const domain = isLocal ? '' : 'domain=.streamio.ai;';
  const secure = isLocal ? '' : 'secure;';
  const sameSite = isLocal ? 'lax' : 'none';
  
  // Set JWT cookie with 2 hour expiration
  // Note: Using .streamio.ai domain allows sharing between www.streamio.ai and web.streamio.ai
  // Use sameSite: 'none' for production to match landing page settings
  // JWT is already properly encoded from URL, so don't double-encode
  document.cookie = `jwt=${jwt}; path=/; ${domain} ${secure} samesite=${sameSite}; max-age=7200`;
};

const clearJwtFromUrl = () => {
  const url = new URL(window.location.href);
  url.searchParams.delete('jwt');
  window.history.replaceState({}, '', url.toString());
};

const validateKindeJwt = (token: string): boolean => {
  console.log('=== WEB APP TOKEN VALIDATION DEBUG ===');
  console.log('Token length:', token.length);
  console.log('Token starts with:', token.substring(0, 20) + '...');
  console.log('Token ends with:', '...' + token.substring(token.length - 20));
  console.log('Token contains dots:', (token.match(/\./g) || []).length);
  console.log('Token format check:', {
    hasThreeParts: token.split('.').length === 3,
    partsLengths: token.split('.').map(part => part.length),
    isBase64Like: /^[A-Za-z0-9+/=]+$/.test(token.replace(/\./g, ''))
  });
  
  try {
    // Check if it's a valid JWT format (3 parts separated by dots)
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.log('Invalid JWT format: not 3 parts');
      console.log('=== END WEB APP DEBUG ===');
      return false;
    }
    
    // Try to decode each part to see what's happening
    try {
      const header = atob(parts[0]);
      console.log('Header decoded successfully, length:', header.length);
    } catch (error) {
      console.log('Failed to decode header:', error);
    }
    
    try {
      const payload = atob(parts[1]);
      console.log('Payload decoded successfully, length:', payload.length);
      const payloadObj = JSON.parse(payload);
      console.log('Payload parsed successfully:', {
        ...payloadObj,
        exp: payloadObj.exp ? new Date(payloadObj.exp * 1000).toISOString() : null
      });
      
      // Check if token is expired
      const currentTime = Date.now() / 1000;
      if (payloadObj.exp && payloadObj.exp < currentTime) {
        console.log('JWT expired:', new Date(payloadObj.exp * 1000));
        console.log('=== END WEB APP DEBUG ===');
        return false;
      }
      
      // Check if token has required fields
      if (!payloadObj.sub || !payloadObj.email) {
        console.log('JWT missing required fields');
        console.log('=== END WEB APP DEBUG ===');
        return false;
      }
      
      console.log('Token validation successful');
      console.log('=== END WEB APP DEBUG ===');
      return true;
    } catch (error) {
      console.log('Failed to decode payload:', error);
      console.log('=== END WEB APP DEBUG ===');
      return false;
    }
  } catch (error) {
    console.error('Error validating JWT:', error);
    console.log('=== END WEB APP DEBUG ===');
    return false;
  }
};

const RequireAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  useEffect(() => {
    console.log('=== REQUIRE AUTH DEBUG ===');
    console.log('Current URL:', window.location.href);
    console.log('URL search params:', window.location.search);
    
    // First check for JWT in URL parameters (from landing page redirect)
    let jwt = getJwtFromUrl();
    console.log('JWT from URL:', jwt ? `${jwt.substring(0, 20)}...` : 'null');
    
    if (jwt) {
      console.log('Found JWT in URL, validating...');
      
      // Validate the JWT before storing
      if (validateKindeJwt(jwt)) {
        console.log('JWT valid, storing in cookie');
        // Store JWT in cookie for future requests
        setJwtCookie(jwt);
        // Clear JWT from URL for security
        clearJwtFromUrl();
      } else {
        console.log('JWT invalid, clearing from URL');
        clearJwtFromUrl();
        jwt = null;
      }
    } else {
      // Fall back to checking cookies
      jwt = getJwtFromCookie();
      console.log('JWT from cookies:', jwt ? `${jwt.substring(0, 20)}...` : 'null');
      console.log('All cookies:', document.cookie);
      
      if (jwt && !validateKindeJwt(jwt)) {
        console.log('Stored JWT invalid, clearing cookies');
        // Clear invalid JWT from cookies
        const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        const sameSite = isLocal ? 'samesite=lax' : 'samesite=none';
        document.cookie = `jwt=; path=/; max-age=0; ${sameSite}`;
        jwt = null;
      }
    }
    
    if (!jwt) {
      console.log('No valid JWT found, redirecting to authentication');
      const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      const isDevelopment = process.env.NODE_ENV === 'development';
      
      // Use environment variable if set, otherwise use environment-appropriate fallback
      let landingUrl = process.env.REACT_APP_LANDING_URL;
      
      if (!landingUrl) {
        if (isLocal || isDevelopment) {
          landingUrl = 'http://localhost:3000';
        } else {
          landingUrl = 'https://www.streamio.ai';
        }
      }
      
      console.log('Redirecting to auth:', {
        landingUrl,
        isLocal,
        isDevelopment,
        hostname: window.location.hostname,
        nodeEnv: process.env.NODE_ENV
      });
      
      window.location.href = `${landingUrl}/api/auth/login`;
    } else {
      console.log('Valid JWT found, user authenticated');
    }
  }, []);

  return <>{children}</>;
};

export default RequireAuth; 